import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanContext } from '../../hooks/usePlanContext';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { API_URL } from '../../config';
import {
  Search,
  FileText,
  FolderOpen,
  ArrowRight,
  Loader2,
  Command,
} from 'lucide-react';
import SearchUpgradePrompt from './SearchUpgradePrompt';

const SearchModal = ({ isOpen, onClose, spaceId = null }) => {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const { context, loading: planLoading } = usePlanContext(currentOrg?.id);

  const searchEnabled =
    !planLoading &&
    (context?.addons?.search === true || context?.features?.algolia_search === true);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBlocked, setSearchBlocked] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSearchBlocked(false);
      setSelectedIndex(0);
      if (searchEnabled) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [isOpen, searchEnabled]);

  const performSearch = useCallback(
    async (searchQuery) => {
      if (!searchEnabled || !searchQuery || searchQuery.length < 2 || !currentOrg) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}/organizations/${currentOrg.id}/search`,
          { query: searchQuery, space_id: spaceId }
        );
        setResults(response.data.results || []);
        setSelectedIndex(0);
        setSearchBlocked(false);
      } catch (error) {
        if (error.response?.status === 403) {
          setSearchBlocked(true);
          setResults([]);
        } else {
          console.error('Search failed:', error);
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentOrg, spaceId, searchEnabled]
  );

  useEffect(() => {
    if (!searchEnabled) return undefined;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch, searchEnabled]);

  const handleKeyDown = (e) => {
    if (!searchEnabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (result) => {
    onClose();
    navigate(`/dashboard/spaces/${result.space_id}?doc=${result.id}`);
  };

  const highlightMatch = (text, q) => {
    if (!q) return text;
    const parts = text.split(new RegExp(`(${q})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const showUpgrade = !planLoading && (!searchEnabled || searchBlocked);

  const handleOpenChange = (open) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden"
        data-testid="search-modal"
        aria-describedby={showUpgrade ? undefined : undefined}
      >
        {showUpgrade ? (
          <>
            <VisuallyHidden.Root asChild>
              <DialogTitle>Advanced Search</DialogTitle>
            </VisuallyHidden.Root>
            <SearchUpgradePrompt onClose={onClose} />
          </>
        ) : planLoading ? (
          <>
            <VisuallyHidden.Root asChild>
              <DialogTitle>Search documents</DialogTitle>
            </VisuallyHidden.Root>
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </>
        ) : (
          <>
            <VisuallyHidden.Root asChild>
              <DialogTitle>Search documents</DialogTitle>
            </VisuallyHidden.Root>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
              <Search className="w-5 h-5 text-slate-400" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search documents..."
                className="border-0 shadow-none focus-visible:ring-0 text-lg placeholder:text-slate-400"
                data-testid="search-input"
              />
              {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
            </div>

            <ScrollArea className="max-h-[400px]">
              {query.length < 2 ? (
                <div className="p-8 text-center text-slate-500">
                  <Command className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-700" />
                  <p className="text-sm">Type at least 2 characters to search</p>
                  <p className="text-xs text-slate-400 mt-2">
                    Press <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">↑</kbd>{' '}
                    <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">↓</kbd> to navigate,{' '}
                    <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">Enter</kbd> to select
                  </p>
                </div>
              ) : results.length === 0 && !loading ? (
                <div className="p-8 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-700" />
                  <p className="text-sm">No documents found for &quot;{query}&quot;</p>
                </div>
              ) : (
                <div className="py-2">
                  {results.map((result, index) => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => handleSelect(result)}
                      className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                        index === selectedIndex
                          ? 'bg-purple-50 dark:bg-purple-900/20'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                      data-testid={`search-result-${index}`}
                    >
                      <div
                        className={`mt-0.5 p-1.5 rounded ${
                          result.match_type === 'title'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900 dark:text-white truncate">
                            {highlightMatch(result.title, query)}
                          </span>
                          {result.match_type === 'title' && (
                            <Badge className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                              Title match
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <FolderOpen className="w-3 h-3" />
                          <span className="truncate">{result.space_name}</span>
                        </div>
                        {result.match_type === 'content' && result.snippet && (
                          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                            {highlightMatch(result.snippet, query)}
                          </p>
                        )}
                      </div>
                      <ArrowRight
                        className={`w-4 h-4 mt-1 transition-opacity ${
                          index === selectedIndex ? 'opacity-100 text-purple-600' : 'opacity-0'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between text-xs text-slate-500">
              <span>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded">
                  Esc
                </kbd>
                to close
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
